/**
 * @deprecated This component is deprecated. Use PartnerTimelineSummary in PartnerTimelinePage instead.
 * The drawer functionality has been replaced with an inline component in the timeline view.
 * Also, the child components PartnerAboutSection and PartnerContactsSection are deprecated.
 */
import { Drawer, Stack, ActionIcon, Group, Image, Box, Select, Divider } from '@mantine/core';
import { Organization, Reference, OrganizationContact } from '@medplum/fhirtypes';
import { useEffect, useRef, useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { PartnerTypePill } from './PartnerTypePill';
import { OrganizationType } from '@bariendo/lib/constants';
import { ORGANIZATION_TYPE_CODING_MAP } from '@bariendo/lib/codings';
import { PartnerAboutSection } from './PartnerAboutSection';
import { PartnerContactsSection } from './PartnerContactsSection';
import { useMedplum } from '@medplum/react';
import { EditableField } from '@/components/EditableField';

function getOrganizationType(org?: Organization): OrganizationType {
  return (org?.type?.[0]?.coding?.[0]?.code as OrganizationType) || 'other';
}

export function PartnerEditDrawer({
  opened,
  onClose,
  orgRef,
}: {
  opened: boolean;
  onClose: () => void;
  orgRef?: Reference<Organization>;
}) {
  const medplum = useMedplum();
  const [loading, setLoading] = useState(true);
  const [organization, setOrganization] = useState<Organization | undefined>();

  useEffect(() => {
    const fetchOrg = async () => {
      if (orgRef) {
        setLoading(true);
        try {
          setOrganization(await medplum.readReference(orgRef));
        } catch (error) {
          // Handle authentication errors gracefully - Medplum will redirect to signin
          // Silently fail to avoid unhandled promise rejection
          // eslint-disable-next-line no-console
          console.error('Failed to fetch organization:', JSON.stringify(error, null, 2));
        } finally {
          setLoading(false);
        }
      }
    };
    fetchOrg();
  }, [medplum, orgRef]);

  const [pickingOrgType, setPickingOrgType] = useState(false);
  const orgTypeSelectRef = useRef<HTMLInputElement>(null);

  const handleTypeChange = async (type: OrganizationType) => {
    if (!organization) return;
    const updatedOrg = await medplum.updateResource({
      ...organization,
      type: [{ coding: [ORGANIZATION_TYPE_CODING_MAP[type]] }],
    });
    setOrganization(updatedOrg);
  };

  const isDrawerOpen = opened && !!organization;

  return (
    <Drawer
      opened={isDrawerOpen}
      onClose={() => {
        onClose();
      }}
      position="right"
      size="xs"
      padding="md"
      styles={{ header: { padding: 0 } }}
      withCloseButton={false}
    >
      {organization && !loading ? (
        <>
          <Drawer.Header>
            <Group>
              <Image src={'https://placehold.co/32x32'} w={32} h={32} radius="md" />
              <Stack gap={0}>
                <EditableField
                  value={organization.name || 'Untitled partner'}
                  onSubmit={async (value) => {
                    const updatedOrg = await medplum.updateResource({ ...organization, name: value });
                    setOrganization(updatedOrg);
                  }}
                  editable={true}
                  inputProps={{ size: 'xs' }}
                  textProps={{ fw: 'bold' }}
                />
                {!pickingOrgType ? (
                  <Box onClick={() => setPickingOrgType(true)}>
                    <PartnerTypePill type={getOrganizationType(organization)} />
                  </Box>
                ) : (
                  <Select
                    size="xs"
                    ref={orgTypeSelectRef}
                    allowDeselect={false}
                    label="Partner type"
                    placeholder="Choose type"
                    data={Object.values(OrganizationType).map((type) => ({
                      value: type,
                      label: ORGANIZATION_TYPE_CODING_MAP[type]?.display || type,
                    }))}
                    defaultValue={getOrganizationType(organization)}
                    onChange={(value) => handleTypeChange(value as OrganizationType)}
                    onBlur={() => setPickingOrgType(false)}
                  />
                )}
              </Stack>
            </Group>
            <ActionIcon
              onClick={onClose}
              variant="subtle"
              color="gray"
              size={12}
              radius="md"
              style={{ position: 'absolute', top: '0', right: '0' }}
            >
              <IconX />
            </ActionIcon>
          </Drawer.Header>
          <Stack>
            <PartnerAboutSection
              organization={organization}
              onChange={async (org) => {
                const updatedOrg = await medplum.updateResource(org);
                setOrganization(updatedOrg);
              }}
            />
            <Divider />
            <PartnerContactsSection
              contacts={organization?.contact || []}
              onChange={async (updatedContacts) => {
                if (!organization) return;
                const updatedOrg = await medplum.updateResource({
                  ...organization,
                  contact: updatedContacts,
                });
                setOrganization(updatedOrg);
              }}
              onAdd={async (newContact: OrganizationContact) => {
                if (!organization) return;
                const updatedOrg = await medplum.updateResource({
                  ...organization,
                  contact: [...(organization.contact || []), newContact],
                });
                setOrganization(updatedOrg);
              }}
            />
          </Stack>
        </>
      ) : null}
    </Drawer>
  );
}
